/**
 * CoverageChangeSection - Section wrapper for coverage change cards
 *
 * Matches the ProviderChangeSection pattern (title + count badge)
 * but renders CoverageChangeCard components.
 * Sorts: CREATED → UPDATED → DELETED → SAME.
 */

import { useMemo } from 'react';

import { Badge } from '@/components/ui/catalyst/badge';
import type { ChangedCoverageEntity } from '@/services/api/generated/crossMatterChangesService';

import { CHANGE_TYPE_ORDER } from './changeTypeConfig';
import { CoverageChangeCard } from './CoverageChangeCard';

interface CoverageChangeSectionProps {
  coverages: ChangedCoverageEntity[];
}

export const CoverageChangeSection = ({
  coverages,
}: CoverageChangeSectionProps) => {
  const sorted = useMemo(
    () =>
      [...coverages].sort(
        (a, b) =>
          CHANGE_TYPE_ORDER[a.change_type] -
          CHANGE_TYPE_ORDER[b.change_type],
      ),
    [coverages],
  );

  if (coverages.length === 0) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <h3 className="text-base font-semibold text-gray-900">Coverages</h3>
        <Badge color="zinc" className="text-xs">
          {coverages.length}
        </Badge>
      </div>
      <div className="space-y-2">
        {sorted.map((coverage) => (
          <CoverageChangeCard
            key={coverage.entity_id}
            entity={coverage}
          />
        ))}
      </div>
    </div>
  );
};
